import { Image, Upload, message } from 'antd'
import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { getFileSizeInMB, getImageBase64, getImageDimensions } from 'src/core/helpers'
import { useAppDispatch, useAppSelector } from 'src/app/hooks'
import type { UploadFile } from 'antd/lib/upload/interface'
import {
  projectFormPicturesConvertedSelector,
  setProjectFormItemValue,
  projectDetailsSelector,
  deleteProjectImage,
} from 'src/features'

const ProjectFormImages: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const pictureFiles = useAppSelector(projectFormPicturesConvertedSelector)
  const { project: projectToEdit } = useAppSelector(projectDetailsSelector)

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState('')

  const beforeUpload = async (file: File) => {
    const isLt2M = getFileSizeInMB(file) < 2
    const dimensions = await getImageDimensions(file)
    const isImageDimensions = dimensions.width >= 1280 || dimensions.height >= 720
    if (!isLt2M) {
      message.error(t('general.MaximumSize'))
    }
    if (!isImageDimensions) {
      message.error(t('general.MinimumResolution'))
    }
    return (isLt2M && isImageDimensions) ||  Upload.LIST_IGNORE
  }

  const onChange = ({ fileList }: { fileList: UploadFile[]}) => {
    const newList = fileList.map((item: UploadFile) => {
      if (item?.originFileObj) return URL.createObjectURL(item?.originFileObj)
      return item?.url
    })
    dispatch(setProjectFormItemValue({ key: 'pictures', value: newList }))
  }

  const onPreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getImageBase64(file.originFileObj)
    }
    setPreviewImage(file.url || (file.preview as string))
    setPreviewOpen(true)
  }

  const handleRemoveImage = (file: UploadFile) => {
    if (projectToEdit) {
      const imageId = projectToEdit?.pictures.find(picture => picture?.image === file?.url)?.id
      if (imageId) { dispatch(deleteProjectImage(imageId)) }
    }
  }

  return (
    <>
      <Upload
        beforeUpload={beforeUpload}
        accept='images/*'
        listType='picture-card'
        fileList={pictureFiles}
        onChange={onChange}
        onPreview={onPreview}
        onRemove={handleRemoveImage}
      >
        {t('Projects.Form.PlusUpload')}
      </Upload>

      <Image
        style={{ display: 'none' }}
        src={previewImage}
        alt='project-image-preview'
        preview={{
          visible: previewOpen,
          src: previewImage,
          onVisibleChange: value => setPreviewOpen(value),
        }}
      />
    </>
  )
}

export default ProjectFormImages
